/**
 * Admin Prefs Service — typed client for the `admin-prefs-manager` function.
 *
 * The client SDK can only manage the CURRENT user's team; this function holds
 * the server API key that lets a superadmin manage ANY team's prefs and any
 * user's labels/prefs. Mirrors the teamInviteService call pattern.
 */

import { Client, ExecutionMethod, Functions } from 'appwrite';

const FUNCTION_ID = 'admin-prefs-manager';

/** Admin-overview team shape (from n8n-proxy admin_client_overview). */
export interface AdminTeamMembership {
  membership_id: string;
  user_id: string | null;
  email: string;
  name: string;
  roles: string[];
  invited_at: string | null;
  joined_at: string | null;
  status: 'accepted' | 'pending';
  /** Present in admin-prefs-manager list_teams responses. */
  labels?: string[];
  /** Lightweight user fields surfaced by list_teams' existing users.get call. */
  access_status?: 'active' | 'inactive' | 'blocked' | 'invited';
  account_enabled?: boolean;
  email_verification?: boolean;
  profile_picture_url?: string | null;
  job_title?: string | null;
  user_created_at?: string | null;
  user_updated_at?: string | null;
}

export interface AdminTeam {
  $id: string;
  name: string;
  $createdAt: string;
  $updatedAt: string;
  total: number;
  prefs: Record<string, any>;
  /** list_teams shape. */
  members?: AdminTeamMembership[];
  /** admin_client_overview shape. */
  memberships?: AdminTeamMembership[];
  /** First confirmed member's email — the identity used to fetch team-wide credits/campaigns. */
  owner_email?: string | null;
  /** Filled client-side by useAdminOverview (per-team N8N lookups). */
  credits?: number | null;
  campaigns_total?: number;
  campaigns_by_status?: { active: number; paused: number; archived: number };
  leads_total?: number;
}

export interface AdminMembership {
  teamId: string;
  teamName: string;
  invited: string;
  joined: string;
  roles: string[];
}

export interface AdminIdentity {
  provider: string;
  providerUid: string;
  providerEmail: string;
  providerAccessTokenExpiry: string;
}

export interface AdminSession {
  $id: string;
  provider: string;
  providerUid: string;
  ip: string;
  countryName: string;
  current: boolean;
  expire: string;
  $createdAt: string;
}

export interface AdminLogEntry {
  event: string;
  ip: string;
  time: string;
  countryName: string;
  userAgent: string;
}

export interface AdminUser {
  $id: string;
  email: string;
  name: string;
  labels: string[];
  prefs: Record<string, any>;
  $createdAt: string;
  $updatedAt: string;
  emailVerification?: boolean;
  status?: string | number | boolean;
  phone?: string;
  /** Roster metadata used by the admin directory. Invitations have no Appwrite user yet. */
  membership_id?: string;
  membership_status?: 'accepted' | 'pending';
  invited_at?: string | null;
  is_invitation?: boolean;
  account_enabled?: boolean;
  /** admin_client_overview: which team this user belongs to. */
  team_id?: string | null;
  /** Extended data from get_user_extended */
  memberships?: AdminMembership[];
  identities?: AdminIdentity[];
  sessions?: AdminSession[];
  logs?: AdminLogEntry[];
}

export interface AdminOverviewData {
  teams: AdminTeam[];
  users: AdminUser[];
  totals: { users: number; teams: number };
}

interface AdminPrefsResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const buildClient = () => new Client()
  .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || '')
  .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || '');

/** Phantom executions: this self-hosted Appwrite occasionally returns an
 *  execution record with status completed / code 200 but an EMPTY responseBody
 *  — the handler never ran and no write happened. Detect and retry. */
const isPhantomExecution = (execution: { status?: string; responseStatusCode?: number; responseBody?: string }): boolean =>
  execution.status === 'completed' &&
  execution.responseStatusCode === 200 &&
  !execution.responseBody?.trim();

const callFunction = async <T>(body: Record<string, unknown>): Promise<T> => {
  const functions = new Functions(buildClient());

  const MAX_ATTEMPTS = 3;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const execution = await functions.createExecution(
      FUNCTION_ID,
      JSON.stringify(body),
      false,
      '/',
      ExecutionMethod.POST,
    );

    // Retry phantom executions — the write never happened server-side.
    if (isPhantomExecution(execution) && attempt < MAX_ATTEMPTS) {
      console.warn(`⚠️ ${FUNCTION_ID}: phantom execution (attempt ${attempt}), retrying…`);
      continue;
    }

    const result: AdminPrefsResult<T> = JSON.parse(execution.responseBody || '{}');
    if (!result.success) {
      throw new Error(result.error || `${FUNCTION_ID} returned failure`);
    }
    return result.data as T;
  }

  throw lastError || new Error(`${FUNCTION_ID}: execution did not complete after ${MAX_ATTEMPTS} attempts`);
};

/** Writable team pref keys — mirrors the function-side allowlist. */
/** Writable team-pref keys — mirrors WRITABLE_TEAM_PREF_KEYS in
 * appwrite-functions/admin-prefs-manager/index.js and TEAM_PREFS_TEMPLATE in
 * services/appwritePrefsSchema.ts. Keep all three in sync. */
export type WritableTeamPrefKey =
  | 'mod_leads' | 'mod_outreach' | 'mod_sales' | 'mod_workers' | 'mod_nexus' | 'mod_chat' | 'mod_goals'
  | 'partner' | 'status'
  | 'lvl1_id' | 'stripe_id' | 'clickup_id' | 'seller_ids' | 'billing_email'
  | 'instantly_org' | 'Instantly_domains'
  | 'unipile_whatsapp_ids' | 'unipile_linkedin_ids'
  | 'twilio_account_id' | 'twilio_phones_ids' | 'elevenlabs_phone_ids'
  | 'currency' | 'country' | 'country_code' | 'state' | 'timezone' | 'language'
  | 'company_website'
  | 'brand_primary_colour' | 'logo_url';

export const adminPrefsService = {
  listTeams(): Promise<{ teams: AdminTeam[] }> {
    return callFunction({ action: 'list_teams', parameters: {} });
  },

  updateTeamPrefs(teamId: string, prefs: Partial<Record<WritableTeamPrefKey, string | boolean | null>>): Promise<{ team_id: string; prefs: Record<string, any> }> {
    return callFunction({ action: 'update_team_prefs', parameters: { team_id: teamId, prefs } });
  },

  getUser(userId: string): Promise<AdminUser> {
    return callFunction({ action: 'get_user', parameters: { user_id: userId } });
  },

  updateUserLabels(userId: string, labels: string[]): Promise<{ user_id: string; labels: string[] }> {
    return callFunction({ action: 'update_user_labels', parameters: { user_id: userId, labels } });
  },

  updateUserPrefs(userId: string, prefs: Record<string, any>): Promise<{ user_id: string; prefs: Record<string, any> }> {
    return callFunction({ action: 'update_user_prefs', parameters: { user_id: userId, prefs } });
  },

  /**
   * Admin-side "Account Settings" for any user: display name + profile
   * picture (base64 — the function uploads it to the profile-pictures bucket
   * under the user's id and stores the URL in prefs via merge-write), plus
   * optional pref updates in the same call.
   */
  updateUserProfile(input: {
    userId: string;
    name?: string;
    /** File to upload as the new avatar; omit to leave unchanged. */
    pictureFile?: File;
    /** Explicitly remove the avatar. */
    removePicture?: boolean;
    prefs?: Record<string, any>;
  }): Promise<{ user_id: string; name: string; prefs: Record<string, any>; profilePictureUrl: string | null }> {
    const { userId, name, pictureFile, removePicture, prefs } = input;
    return (async () => {
      let profile_picture: { base64: string; mimeType: string } | null | undefined;
      if (pictureFile) {
        const buffer = new Uint8Array(await pictureFile.arrayBuffer());
        // chunked btoa — avoids call-stack limits on large strings
        let binary = '';
        for (let i = 0; i < buffer.length; i += 0x8000) {
          binary += String.fromCharCode(...buffer.subarray(i, i + 0x8000));
        }
        profile_picture = { base64: btoa(binary), mimeType: pictureFile.type || 'image/jpeg' };
      } else if (removePicture) {
        profile_picture = null;
      }
      return callFunction({
        action: 'update_user_profile',
        parameters: {
          user_id: userId,
          ...(name !== undefined ? { name } : {}),
          ...(profile_picture !== undefined ? { profile_picture } : {}),
          ...(prefs ? { prefs } : {}),
        },
      });
    })();
  },
};
